import Box from "@mui/material/Box";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import { APP_CONTENT_GRID } from "../appLayout";
import type { FC } from "react";
import { tokens } from "../../theme/tokens";
import { CubWordmark } from "../CubWordmark";
import { APP_HEADER_TITLES, LABELS } from "./content";
import type { AppHeaderApp, AppHeaderProps } from "./types";

/* ── Medidas da spec (Figma `<headerStatementViewer>`, node 3873:10314) ───── */

/**
 * A altura é fixa nas 12 variantes — o header não cresce com o conteúdo. Por
 * isso o padding Y de 24px da spec é folga máxima, não medida efetiva: no
 * mobile o logo de 48px ocupa mais que os 32px que sobram, e fica centrado
 * dentro dos 80px (é o que o Figma mostra).
 */
const HEIGHT_DESKTOP = "112px";
const HEIGHT_MOBILE = "80px";
/**
 * Raio do canto da arte dos logos de parceiro: 4px numa tela de 140px = 2,86%.
 * Em porcentagem, acompanha a caixa — ver `companyLogoRadius` nos tipos.
 */
const PARTNER_LOGO_ART_RADIUS = "2.86%";

const PAD_Y = "24px";

/** Gap da linha raiz (marca ↔ informação). */
const GAP_ROOT_DESKTOP = "24px";
const GAP_ROOT_MOBILE = "16px";
/** Gap interno do bloco de informação (texto ↔ logo) — 16px nos dois. */
const GAP_TENANT = "16px";
const WORDMARK_WIDTH_DESKTOP = 80;
const WORDMARK_WIDTH_MOBILE = 60;
const COMPANY_LOGO_DESKTOP = "64px";
const COMPANY_LOGO_MOBILE = "48px";
const BACK_BUTTON_SIZE = "32px";
const BACK_BUTTON_PAD = "2px";
const BACK_ICON_SIZE = 28;

/**
 * **O `billing-viewer` é o único fixo no topo**, e por isso o único com sombra
 * — ele passa por cima do conteúdo que rola. Os demais rolam junto com a
 * página e encostam no conteúdo, sem sombra.
 *
 * Duas consequências de uma decisão só, então é uma função só: quem é fixo é
 * quem tem sombra.
 *
 * Escrita como **lista de permissão** de propósito, não como negação dos
 * outros: é o que faz cada app novo do eixo nascer rolando sem sombra, sem
 * precisar de uma linha. Inverter para `app !== "statement-viewer"` daria o
 * mesmo resultado hoje e o resultado errado no próximo valor.
 */
const isPinned = (app: AppHeaderApp) => app === "billing-viewer";

/**
 * **O `statement-viewer` é o único que aceita botão de voltar** — `onBack` é
 * ignorado nos demais, e por motivos diferentes: do boleto não há de onde
 * voltar; na revisão de dados o header é o começo do fluxo, não uma parada
 * dentro dele.
 *
 * Também **lista de permissão**, pela mesma razão do `isPinned`: app novo
 * nasce sem o botão, e sem slot algum no DOM — não é um botão oculto, é a
 * ausência dele.
 */
const acceptsBackButton = (app: AppHeaderApp) => app === "statement-viewer";

/* ── Componente ──────────────────────────────────────────────────────────── */

/**
 * Header das aplicações **Área do Cliente** (`statement-viewer`),
 * **Visualizador de Cobranças** (`billing-viewer`) e **Revisão de dados**
 * (`onboarding-app`), nas variações CUB e white label, desktop e mobile. Par
 * do `AppFooter` — mesmos eixos (`tenant` × mobile), mesmo padding X por app,
 * mesmo mobile por `useMediaQuery`.
 *
 * A superfície é sempre branca: quem muda de cor por tenant é o footer, não o
 * header. O que o tenant muda é o **arranjo** — com o wordmark da CUB a
 * informação vai para a direita; sem ele, ancora à esquerda.
 *
 * O que o **app** muda vai além do título e do padding, e não é um par de
 * opostos: fixo-no-topo e aceita-voltar são eixos independentes. O
 * `billing-viewer` é o único fixo no topo (`sticky`) com sombra; o
 * `statement-viewer` é o único que aceita `onBack`; o `onboarding-app` rola
 * com a página **e** não tem botão de voltar — o header é o começo do fluxo de
 * revisão, não uma parada dentro dele.
 *
 * ⚠️ **`onboarding-app` existe só no eixo do header.** A revisão de dados
 * monta o footer com `app="statement-viewer"`, então numa mesma página os dois
 * recebem nomes diferentes — de propósito, não por engano. O `app` do
 * `AppFooter` não seleciona "qual aplicação", seleciona **qual conjunto de
 * conteúdo institucional** (extrato × boleto), e duas aplicações podem
 * legitimamente dividir o mesmo.
 */
export const AppHeader: FC<AppHeaderProps> = ({
  app,
  tenant = "cub",
  title,
  companyName,
  companyLogoSrc,
  companyLogoRadius = PARTNER_LOGO_ART_RADIUS,
  companyLogoAlt,
  onBack,
  backLabel = LABELS.back,
  contentMaxWidth,
  contentGutter,
  mobileBreakpoint = "md",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down(mobileBreakpoint));
  // Grid do paper da aplicação — o DS é dono do número; ver `appLayout.ts`.
  const grid = APP_CONTENT_GRID[app];

  const isCub = tenant === "cub";

  // Texto vazio/em branco conta como ausente — é o que chega de API e é o que
  // o controle do Storybook produz ao limpar o campo. Mesma regra do footer.
  const filled = (v?: string) => (v && v.trim() ? v : undefined);

  const resolvedTitle = title ?? APP_HEADER_TITLES[app];
  const company = filled(companyName);
  const logoSrc = filled(companyLogoSrc);

  /* ── Botão de voltar ── */

  const backButton =
    onBack && acceptsBackButton(app) ? (
      <IconButton
        onClick={onBack}
        aria-label={backLabel}
        sx={{
          flexShrink: 0,
          width: BACK_BUTTON_SIZE,
          height: BACK_BUTTON_SIZE,
          padding: BACK_BUTTON_PAD,
          borderRadius: tokens.radiusMd,
          border: `1px solid ${tokens.blue}`,
          color: tokens.blue,
        }}
      >
        <Icon sx={{ fontSize: `${BACK_ICON_SIZE}px` }}>chevron_left</Icon>
      </IconButton>
    ) : null;

  /* ── Marca da plataforma (só no tenant CUB) ── */

  const wordmark = isCub ? (
    <Box sx={{ display: "flex", flexShrink: 0, color: tokens.blue }}>
      <CubWordmark
        width={isMobile ? WORDMARK_WIDTH_MOBILE : WORDMARK_WIDTH_DESKTOP}
      />
    </Box>
  ) : null;

  /* ── Informação da aplicação e da company ── */

  const info = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        // A spec alinha à direita no tenant CUB (o wordmark ocupa a esquerda) e
        // à esquerda no white label, onde o bloco é a própria marca.
        alignItems: isCub ? "flex-end" : "flex-start",
        flex: "1 0 0",
        minWidth: 0,
        overflow: "hidden",
      }}
    >
      <Typography
        sx={{
          fontFamily: tokens.fontBody,
          fontSize: `${isMobile ? tokens.fontSize14 : tokens.fontSize16}px`,
          fontWeight: 700,
          lineHeight: 1.5,
          color: tokens.textPrimary,
          maxWidth: "100%",
          // O Figma desliga o truncamento do título, mas ele é `nowrap`: num
          // título longo de parceiro somado ao botão de voltar, isso vazaria
          // para fora do header. O reticências é degradação, não mudança —
          // em todas as 12 variantes da spec o título cabe inteiro.
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {resolvedTitle}
      </Typography>
      {company && (
        <Typography
          sx={{
            fontFamily: tokens.fontBody,
            fontSize: `${isMobile ? tokens.fontSize12 : tokens.fontSize14}px`,
            fontWeight: 400,
            lineHeight: 1.5,
            color: tokens.textSecondary,
            width: "100%",
            textAlign: isCub ? "right" : "left",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {company}
        </Typography>
      )}
    </Box>
  );

  const companyLogo = logoSrc ? (
    <Box
      component="img"
      src={logoSrc}
      alt={companyLogoAlt ?? ""}
      sx={{
        display: "block",
        flexShrink: 0,
        width: isMobile ? COMPANY_LOGO_MOBILE : COMPANY_LOGO_DESKTOP,
        height: isMobile ? COMPANY_LOGO_MOBILE : COMPANY_LOGO_DESKTOP,
        // Ver `companyLogoRadius`: sem raio por default, para não cortar a
        // moldura que a arte do parceiro já tem.
        borderRadius: companyLogoRadius,
        objectFit: "cover",
      }}
    />
  ) : null;

  return (
    <Box
      component="header"
      sx={{
        boxSizing: "border-box",
        width: "100%",
        height: isMobile ? HEIGHT_MOBILE : HEIGHT_DESKTOP,
        backgroundColor: tokens.white,
        fontFamily: tokens.fontBody,
        // `sticky`, não `fixed`: fixo no topo desde o começo da rolagem dá o
        // mesmo resultado visual, mas continua ocupando lugar no fluxo — com
        // `fixed` a aplicação teria de compensar a altura com padding, e
        // errar esse padding esconde a primeira linha do conteúdo.
        ...(isPinned(app) && {
          position: "sticky",
          top: 0,
          zIndex: theme.zIndex.appBar,
          boxShadow: tokens.shadow4,
        }),
      }}
    >
      {/* A faixa branca atravessa a viewport; o wrapper posiciona o conteúdo.
          Com `contentMaxWidth`, alinha com o paper da página por construção. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: isMobile ? GAP_ROOT_MOBILE : GAP_ROOT_DESKTOP,
          boxSizing: "border-box",
          width: "100%",
          height: "100%",
          paddingBlock: PAD_Y,
          paddingInline:
            contentGutter ?? `${isMobile ? grid.gutterMobile : grid.gutter}px`,
          maxWidth: contentMaxWidth ?? `${grid.maxWidth}px`,
          marginInline: "auto",
        }}
      >
        {backButton}
        {wordmark}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: GAP_TENANT,
            flex: "1 0 0",
            minWidth: 0,
            // No tenant CUB o bloco encosta na direita, longe do wordmark.
            justifyContent: isCub ? "flex-end" : "flex-start",
          }}
        >
          {isCub ? (
            <>
              {info}
              {companyLogo}
            </>
          ) : (
            <>
              {companyLogo}
              {info}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppHeader;
